/* eslint-disable react/prop-types */
import ErrorState from 'pages/ErrorState'
import PropTypes from 'prop-types'
import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'

import ConversionReportTabs from 'components/ConversionReportTabs'
import ErrorBoundary from 'components/ErrorBoundary'
import Loader from 'components/Loader'
import ManagementConversionEvents from 'components/ManagementConversionEvents'
import EditEvents from 'components/ManagementConversionEvents/EditEvents'
import MonitoringNumbers from 'components/MonitoringNumbers'
import { ACCOUNTS_CONNECTION_ROUTES, CONVERSION_REPORT_ROUTES, HSM_REPORT_ROUTES, MODEL_ROUTES } from 'constants/index'
import AccountsStateProvider from 'contexts/AccountsStateProvider'
import AnalyticsReportsProvider from 'contexts/AnalyticsReportsProvider'
import ConversionReportProvider from 'contexts/conversion-report/provider'
import HsmReportsProvider from 'contexts/hsm-report/provider'
import { ModelProvider } from 'contexts/models-context'
import { SentMessagesProvider } from 'contexts/sentMessages-context'
import ConversionReport from 'pages/ConversionReport'
import ExportHistoryPage from 'pages/ExportHistory'
import Hsm from 'pages/Hsm'
import Callback from 'pages/TikTok'
import { WhatsappProfileSkeleton } from 'pages/WhatsappProfile/Skeleton'
import ProtectedRoutes from './ProtectedRoutes'
import { LocalhostNav } from './LocalhostNav'

const FlowsPage = lazy(() => import('pages/Flows'))
const LazyMessageTemplateForm = lazy(() => import('pages/MessageTemplateForm'))
const LazySendMessage = lazy(() => import('pages/SendMessage'))
const SentMessageList = lazy(() => import('pages/SentMessagesList'))
const ModelList = lazy(() => import('components/ModelListContainer'))
const Model = lazy(() => import('pages/Model'))
const ModelLayoutPage = lazy(() => import('pages/ModelList'))
const HsmReports = lazy(() => import('components/HsmReports'))
const EmptyModel = lazy(() => import('pages/EmptyModel'))
const EmptySentMessages = lazy(() => import('pages/EmptySentMessages'))
const AccountsSelfConnection = lazy(() => import('pages/AccountsSelfConnection'))
const LazyAccountsConnection = lazy(() => import('pages/AccountsConnection'))
const LazyAccountList = lazy(() => import('components/AccountsConnection/AccountList'))
const AnalyticsReports = lazy(() => import('pages/AnalyticsReports'))
const LazyWhatsappProfile = lazy(() => import('pages/WhatsappProfile'))

function LazyRoute({ children, fallback }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  )
}

LazyRoute.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node.isRequired,
}

export default function AllRoutes() {
  return (
    <Suspense fallback={<Loader />}>
      <LocalhostNav />
      <Routes>
        <Route path="/" element={<ProtectedRoutes />}>
          <Route
            path="whatsapp-resources"
            element={
              <ModelProvider>
                <LazyRoute fallback={<Loader />}>
                  <ModelLayoutPage />
                </LazyRoute>
              </ModelProvider>
            }
          >
            <Route
              path={MODEL_ROUTES.MODEL_LIST}
              element={
                <LazyRoute fallback="Carregando listagem de modelos...">
                  <ModelList />
                </LazyRoute>
              }
            />
            <Route
              path={MODEL_ROUTES.SENT_MESSAGE_LIST}
              element={
                <SentMessagesProvider>
                  <LazyRoute fallback="Carregando listagem de mensagens disparadas...">
                    <SentMessageList />
                  </LazyRoute>
                </SentMessagesProvider>
              }
            />
            <Route
              path={MODEL_ROUTES.FLOWS}
              element={
                <LazyRoute fallback="Carregando listagem de flows...">
                  <FlowsPage />
                </LazyRoute>
              }
            />
          </Route>
          <Route
            path={MODEL_ROUTES.SEND_MESSAGE}
            element={
              <ModelProvider>
                <SentMessagesProvider>
                  <LazyRoute fallback={<Loader />}>
                    <LazySendMessage />
                  </LazyRoute>
                </SentMessagesProvider>
              </ModelProvider>
            }
          />
          <Route
            path={MODEL_ROUTES.CREATE_MODEL_PARTNER}
            element={
              <ModelProvider>
                <LazyRoute fallback={<Loader />}>
                  <Model />
                </LazyRoute>
              </ModelProvider>
            }
          />
          <Route
            path={MODEL_ROUTES.CREATE_MODEL_HI}
            element={
              <ModelProvider>
                <LazyRoute fallback={<Loader />}>
                  <LazyMessageTemplateForm />
                </LazyRoute>
              </ModelProvider>
            }
          />
          <Route
            path="tiktok-callback"
            element={
              <LazyRoute fallback="Processando callback do TikTok...">
                <Callback />
              </LazyRoute>
            }
          />
          <Route
            path="emptySentMessages"
            element={
              <LazyRoute fallback="Carregando página de mensagens vazias...">
                <EmptySentMessages />
              </LazyRoute>
            }
          />
          <Route
            path={ACCOUNTS_CONNECTION_ROUTES.ACCOUNTS}
            element={
              <AccountsStateProvider>
                <LazyRoute fallback="Carregando conexão de conta...">
                  <LazyAccountsConnection />
                </LazyRoute>
              </AccountsStateProvider>
            }
          />
          <Route
            path={ACCOUNTS_CONNECTION_ROUTES.MANAGEMENT_WHATSAPP_ACCOUNTS}
            element={
              <AccountsStateProvider>
                <LazyRoute fallback="Carregando lista de conexões de contas...">
                  <LazyAccountList />
                </LazyRoute>
              </AccountsStateProvider>
            }
          />
          <Route
            path="accountsSelfConnection"
            element={
              <AccountsStateProvider>
                <ConversionReportProvider>
                  <LazyRoute fallback="Carregando conexão de conta...">
                    <AccountsSelfConnection />
                  </LazyRoute>
                </ConversionReportProvider>
              </AccountsStateProvider>
            }
          />
          <Route
            path="emptyModel"
            element={
              <LazyRoute fallback="Carregando página de modelo vazio...">
                <EmptyModel />
              </LazyRoute>
            }
          />

          <Route path="relatorios">
            <Route
              path="hsm"
              element={
                <LazyRoute fallback="Carregando relatório de HSM...">
                  <Hsm />
                </LazyRoute>
              }
            >
              <Route
                path={HSM_REPORT_ROUTES.REPORT}
                element={
                  <HsmReportsProvider>
                    <LazyRoute fallback="Carregando relatório de HSM...">
                      <HsmReports />
                    </LazyRoute>
                  </HsmReportsProvider>
                }
              />
              <Route
                path={HSM_REPORT_ROUTES.MONITORING_NUMBERS}
                element={
                  <LazyRoute fallback="Carregando números monitorados...">
                    <MonitoringNumbers />
                  </LazyRoute>
                }
              />
            </Route>
            <Route
              path="analyticReport"
              element={
                <AnalyticsReportsProvider>
                  <LazyRoute fallback="Carregando relatório analítico...">
                    <AnalyticsReports />
                  </LazyRoute>
                </AnalyticsReportsProvider>
              }
            />
            <Route
              path="whatsapp-export-history"
              element={
                <LazyRoute fallback="Carregando histórico de exportação...">
                  <ExportHistoryPage />
                </LazyRoute>
              }
            />

            <Route
              path="conversion"
              element={
                <ConversionReportProvider>
                  <LazyRoute fallback="Carregando relatório de conversão...">
                    <ConversionReport />
                  </LazyRoute>
                </ConversionReportProvider>
              }
            >
              <Route
                path={CONVERSION_REPORT_ROUTES.REPORT}
                element={
                  <LazyRoute fallback="Carregando relatório de conversão...">
                    <ConversionReportTabs />
                  </LazyRoute>
                }
              />
              <Route
                path={CONVERSION_REPORT_ROUTES.MANAGEMENT_EVENTS}
                element={
                  <LazyRoute fallback="Carregando eventos de conversão...">
                    <ManagementConversionEvents />
                  </LazyRoute>
                }
              />
            </Route>
          </Route>
          <Route
            path={CONVERSION_REPORT_ROUTES.EVENTS_ENABLE}
            element={
              <ConversionReportProvider>
                <LazyRoute fallback="Carregando eventos de conversão...">
                  <EditEvents />
                </LazyRoute>
              </ConversionReportProvider>
            }
          />
          <Route
            path={CONVERSION_REPORT_ROUTES.EVENTS_EDIT}
            element={
              <ConversionReportProvider>
                <LazyRoute fallback="Carregando edição de eventos de conversão...">
                  <EditEvents />
                </LazyRoute>
              </ConversionReportProvider>
            }
          />
          <Route
            path="/whatsapp-profile"
            element={
              <LazyRoute fallback={<WhatsappProfileSkeleton />}>
                <LazyWhatsappProfile />
              </LazyRoute>
            }
          />
        </Route>

        <Route
          path="*"
          element={
            <LazyRoute fallback={<Loader />}>
              <ErrorState />
            </LazyRoute>
          }
        />
      </Routes>
    </Suspense>
  )
}
